// client/src/components/pages/LexiStar/Replay/replayDisplay.ts
//
// Replay drawn with the organisation's display rules (ST-00276 phase 2, map colours & states).
// Pure. At every replayed moment the rule version IN FORCE THEN (GET /display-rules/versions:
// spans with valid_from / valid_to) is evaluated over what Replay has at that moment
// (map/displayRules factsOfReplayMoment): the trip stage (and the context it implies), the
// point's speed, ignition and fuel level, and the events up to that moment. Fields Replay does not
// keep historically (the engine's motion, offline, maintenance, zones, fuel figures, most device
// readings) read as missing; the legend names the ones the organisation's own rules use. Events
// are read as the live board reads them: voided ones left out, and the events before the window
// within the rules' look-back (the versions endpoint's `events_before`) count from the start.
//
//   displayBands(replay, spans)   consecutive stretches with one display (token + marks)
//   displayAt(bands, t)           the display at the replay clock (binary search)
//   displayTrackFeatures(...)     the track sliced by display, in each token's map colour
//   replayDisplayLegend(spans)    the legend of the rules in force + the fields not shown
// With no spans (no access, an older server) everything falls back to the default rules, which
// give exactly phase 1's colours by stage (the golden test).

import type { Feature, FeatureCollection, LineString } from 'geojson';
import { ASSET_STATUS, type AssetStatusKey } from '../../../map/assetStatus';
import { compiledOf } from '../../../map/assetDisplay';
import {
  DEFAULT_DISPLAY_RULES,
  evaluateDisplay,
  factsOfReplayMoment,
  fieldLabel,
  fieldsNotInReplay,
  legendOf,
  sameRule,
  versionAt,
  type AssetDisplay,
  type CompiledRules,
  type DisplayRule,
  type LegendEntry,
  type RuleVersionSpan,
} from '../../../map/displayRules';
import { sliceTrack } from './replayLogic';
import type { ReplayData } from '../../../../types/lexistar-replay';

export interface DisplayBand {
  from: number;
  to: number;
  display: AssetDisplay;
}

/** The defaults, in force always (no spans). */
const DEFAULT_SPANS: RuleVersionSpan[] = [
  { version: 0, valid_from: null, valid_to: null, rules: DEFAULT_DISPLAY_RULES },
];

const sameDisplay = (a: AssetDisplay, b: AssetDisplay): boolean =>
  a.base.token === b.base.token &&
  a.base.rule_id === b.base.rule_id &&
  a.rules_version === b.rules_version &&
  a.overlays.length === b.overlays.length &&
  a.overlays.every((o, i) => o.rule_id === b.overlays[i]?.rule_id);

/** The stage band at t (bands sorted by `from`), or null. */
function stageAt(bands: ReplayData['stage_bands'], t: number): string | null {
  let lo = 0;
  let hi = bands.length - 1;
  let found: string | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const b = bands[mid]!;
    if (b.from <= t) {
      if (t <= b.to) found = b.stage ?? null;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return found;
}

/**
 * Consecutive stretches of the replay with one display, by the version in force at each moment.
 * Moments: every track point, plus every stage band's start (a stage without fixes still counts).
 */
export function displayBands(
  replay: Pick<ReplayData, 'track' | 'stage_bands' | 'events'>,
  spans: readonly RuleVersionSpan[] | null | undefined,
  eventsBefore: ReadonlyArray<{ type: string; at: string }> = []
): DisplayBand[] {
  const inForce = spans && spans.length ? spans : DEFAULT_SPANS;
  const compiledBySpan = new Map<RuleVersionSpan, CompiledRules>();
  const compiledAt = (t: number): CompiledRules => {
    const span = versionAt(inForce, t) as RuleVersionSpan;
    let c = compiledBySpan.get(span);
    if (!c) compiledBySpan.set(span, (c = compiledOf(span.rules, span.version)));
    return c;
  };
  const bands = [...replay.stage_bands].filter((b) => Number.isFinite(b.from)).sort((a, b) => a.from - b.from);
  const points = replay.track.filter((p) => Number.isFinite(p.t));
  const moments: Array<{ t: number; speed: number | null; ignition: boolean | null; fuel: number | null }> = [
    ...points.map((p) => ({ t: p.t, speed: p.speed_kmh, ignition: p.ignition, fuel: p.fuel_pct })),
    ...bands.map((b) => ({ t: b.from, speed: null, ignition: null, fuel: null })),
  ].sort((a, b) => a.t - b.t);
  const events = replay.events
    .filter((e) => Number.isFinite(e.t) && typeof e.type === 'string' && e.status !== 'voided')
    .sort((a, b) => a.t - b.t);
  // The events before the window, in the rules' look-back: they count from the first moment.
  const seen: Record<string, number> = {};
  for (const b of eventsBefore) {
    const t = Date.parse(b.at);
    if (Number.isFinite(t) && !(seen[b.type] !== undefined && (seen[b.type] as number) >= t)) seen[b.type] = t;
  }
  let e = 0;
  const out: DisplayBand[] = [];
  for (const m of moments) {
    // The events up to this moment (the newest of each type).
    while (e < events.length && events[e]!.t <= m.t) {
      seen[events[e]!.type as string] = events[e]!.t;
      e++;
    }
    const facts = factsOfReplayMoment({ stage: stageAt(bands, m.t), speed_kmh: m.speed, ignition: m.ignition, fuel_pct: m.fuel });
    const display = evaluateDisplay(compiledAt(m.t), facts, seen, m.t);
    const last = out[out.length - 1];
    if (last && sameDisplay(last.display, display)) last.to = m.t;
    else {
      if (last) last.to = m.t;
      out.push({ from: m.t, to: m.t, display });
    }
  }
  return out;
}

/** The display at t (the band that holds it; before the first, the first). */
export function displayAt(bands: readonly DisplayBand[], t: number): AssetDisplay | null {
  if (!bands.length) return null;
  let lo = 0;
  let hi = bands.length - 1;
  let found = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (bands[mid]!.from <= t) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return bands[found]!.display;
}

/** The track sliced by display, each slice in its token's map colour. */
export function displayTrackFeatures(
  track: ReplayData['track'],
  bands: readonly DisplayBand[]
): FeatureCollection<LineString, Record<string, unknown>> {
  const features: Feature<LineString, Record<string, unknown>>[] = [];
  for (const b of bands) {
    const coords = sliceTrack(track, b.from, b.to);
    if (coords.length < 2) continue;
    features.push({
      type: 'Feature',
      properties: {
        kind: 'display',
        token: b.display.base.token,
        rule: b.display.base.rule_id,
        color: ASSET_STATUS[b.display.base.token as AssetStatusKey].marker,
      },
      geometry: { type: 'LineString', coordinates: coords },
    });
  }
  return { type: 'FeatureCollection', features };
}

const DEFAULT_BY_ID = new Map(DEFAULT_DISPLAY_RULES.map((r) => [r.id, r]));
/** The organisation's own rules: new ones, and defaults it changed. */
export const ownRules = (rules: readonly DisplayRule[]): DisplayRule[] =>
  rules.filter((r) => {
    const d = DEFAULT_BY_ID.get(r.id);
    return !d || !sameRule(d, r);
  });

export interface ReplayDisplayLegend {
  /**
   * The legend of every rule set in force over the replay (by id, the latest wins), less the
   * colour rules of your own and the marks that read a field Replay does not keep (they cannot
   * show here; `notShown` names the fields of your own).
   */
  entries: LegendEntry[];
  /** The words of the fields the organisation's own rules read that Replay does not keep. */
  notShown: string[];
  /** Some rule set in force is not the code defaults. */
  custom: boolean;
}

export function replayDisplayLegend(spans: readonly RuleVersionSpan[] | null | undefined): ReplayDisplayLegend {
  const inForce = spans && spans.length ? spans : DEFAULT_SPANS;
  const byId = new Map<string, LegendEntry>();
  const notShown = new Set<string>();
  let custom = false;
  for (const s of inForce) {
    const historical = new Set(s.rules.filter((r) => fieldsNotInReplay([r]).length === 0).map((r) => r.id));
    for (const e of legendOf(s.rules)) {
      if (e.kind !== 'base' && !historical.has(e.id)) continue;
      byId.set(`${e.kind}:${e.id}`, e);
    }
    const own = ownRules(s.rules);
    // Custom: a rule of its own, a default changed, or a default left out.
    if (own.length > 0 || s.rules.length !== DEFAULT_DISPLAY_RULES.length) custom = true;
    for (const id of fieldsNotInReplay(own)) notShown.add(fieldLabel(id));
  }
  return { entries: [...byId.values()], notShown: [...notShown].sort(), custom };
}
